export interface RecipeConfig {
  name: string;
  /** Shown in recipe pickers and summary cards; falls back to `name` when omitted. */
  title?: string;
  /** Short labels shown on recipe tiles (e.g. category, workload). */
  tags?: string[];
  logoUrl?: string;
  description: string;
  storage: string;
  ram: string;
  resource_private_name?: string;
  duration_hours?: number;
  gpus_per_container?: number;
  hardware_name?: string;
  /** Minimum VRAM per GPU card required (GB). Used to filter hardware selection. */
  min_vram_per_card?: number;
  node_pool_id?: string;
  container_config?: {
    replica_count?: number;
    env_variables?: Record<string, string>;
    secret_env_variables?: Record<string, string>;
    entrypoint?: string[];
    traffic_port?: number;
    args?: string[];
    command?: string | string[];
  };
  registry_config?: {
    image_url?: string;
    registry_username?: string;
    registry_secret?: string;
  };
  healthCheckPath?: string;
}

export const FIXED_FIRST_RECIPE = "DeepSeek-V4-Flash";

export const RECIPES: Record<string, RecipeConfig> = {
  /* Legacy recipes — kept for reference; not shown in UI

  "gpt-oss-20b": {
    name: "gpt-oss-20b",
    title: "GPT-OSS 20B",
    tags: ["Experimental", "OpenAI-compatible"],
    logoUrl: "/assets/images/recipes/gpt-oss-20b.webp",
    description:
      "Experimental high-performance LLM optimized for low-latency specialized tasks. Ideal for fine-tuning or private RAG workflows.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    resource_private_name: "gpt-oss-20b-PROD-test",
    duration_hours: 1,
    gpus_per_container: 1,
    node_pool_id: "54f7e4e9-f23e-443f-b6a5-042112c4e6fa",
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "openai/gpt-oss-20b",
        "--tool-call-parser",
        "openai",
        "--enable-auto-tool-choice",
        "--max-model-len",
        "64000",
        "--enforce-eager",
        "--enable-prefix-caching",
        "--gpu-memory-utilization",
        "0.95",
        "--max-num-seqs",
        "64",
        "--tensor-parallel-size",
        "1",
        "--enable-prompt-tokens-details"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "qwen2.5-coder-7b": {
    name: "qwen2.5-coder-7b",
    title: "Qwen 2.5 Coder 7B",
    tags: ["Coding", "32K context"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Qwen.png",
    description:
      "Best-in-class coding assistant. Exceptional at Python, Java, and C++ generation with a 32K context window.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "Qwen/Qwen2.5-Coder-7B-Instruct",
        "--max-model-len",
        "32768",
        "--enforce-eager",
        "--enable-prefix-caching",
        "--gpu-memory-utilization",
        "0.95",
        "--max-num-seqs",
        "128",
        "--tensor-parallel-size",
        "1",
        "--enable-prompt-tokens-details"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "GLM-4.6": {
    name: "GLM-4.6",
    title: "GLM 4.6",
    tags: ["Recommended", "Tool calling"],
    logoUrl:
      "https://cdn-avatars.huggingface.co/v1/production/uploads/62dc173789b4cf157d36ebee/i_pxzM2ZDo3Ub-BEgIkE9.png",
    description:
      "Top-tier open-weight model with exceptional performance in bilingual tasks and autonomous function calling. Optimized via vLLM.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: ["python3", "-m", "vllm.entrypoints.openai.api_server"],
      args: [
        "--model",
        "zai-org/GLM-4.6",
        "--tensor-parallel-size",
        "8",
        "--enable-prompt-tokens-details",
        "--enable-auto-tool-choice",
        "--enable-prefix-caching",
        "--tool-call-parser",
        "glm45",
        "--enable-expert-parallel",
        "--trust-remote-code"
      ]
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Mistral-7B-Instruct-v0.3": {
    name: "Mistral-7B-Instruct-v0.3",
    title: "Mistral 7B Instruct",
    tags: ["General chat", "Fast inference"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Ministral.png",
    description:
      "The gold standard for 7B models. Balanced, fast, and highly reliable for general chat and instruction following.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "mistralai/Mistral-7B-Instruct-v0.3",
        "--dtype",
        "float16",
        "--max-model-len",
        "16384",
        "--enable-prefix-caching",
        "--gpu-memory-utilization",
        "0.90",
        "--max-num-seqs",
        "64",
        "--tensor-parallel-size",
        "1",
        "--enable-prompt-tokens-details"
      ]
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  jupyter: {
    name: "Jupyter",
    title: "Jupyter Notebook",
    tags: ["Data science", "Interactive"],
    logoUrl: "https://docs.jupyter.org/en/latest/_static/favicon.png",
    description:
      "The gold standard for data science. Mix live code, equations, and rich visualizations in a high-performance sandbox optimized for exploratory data analysis.",
    storage: "Any",
    ram: "Any",
    healthCheckPath: "/api",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "start-notebook.sh",
        "--ServerApp.token=",
        "--ServerApp.password=",
        "--ServerApp.allow_origin=*",
        "--ServerApp.ip=0.0.0.0",
        "--ServerApp.port=8000",
        "--ServerApp.open_browser=False",
        "--ServerApp.disable_check_xsrf=True"
      ]
    },
    registry_config: {
      image_url: "jupyter/datascience-notebook:x86_64-ubuntu-22.04",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Kimi-K2-Instruct-0905": {
    name: "Kimi-K2-Instruct-0905",
    title: "Kimi K2 Instruct",
    tags: ["Agentic", "H200"],
    logoUrl:
      "https://framerusercontent.com/images/EWOn1OXFdxC9nCqiWn4XVhEdo.png?width=512&height=512",
    description:
      "Kimi's latest K2 Instruct 0905 model. State-of-the-art open source model performance.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/bin/bash",
        "-c",
        "python3 -m pip install --no-cache-dir blobfile && python3 -m vllm.entrypoints.openai.api_server --model moonshotai/Kimi-K2-Instruct-0905 --tensor-parallel-size 8   --enable-prompt-tokens-details   --enable-auto-tool-choice   --enable-prefix-caching   --enable-expert-parallel   --max-model-len 32768   --gpu-memory-utilization 0.95   --tool-call-parser kimi_k2   --trust-remote-code"
      ]
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Kimi-K2-Thinking": {
    name: "Kimi-K2-Thinking",
    title: "Kimi K2 Thinking",
    tags: ["Reasoning", "Chain-of-thought"],
    logoUrl:
      "https://framerusercontent.com/images/EWOn1OXFdxC9nCqiWn4XVhEdo.png?width=512&height=512",
    description:
      "Reasoning-optimized model. Best for complex logic, math, and multi-step problem solving using native Chain-of-Thought.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/bin/bash",
        "-c",
        "python3 -m pip install --no-cache-dir blobfile && python3 -m vllm.entrypoints.openai.api_server --model moonshotai/Kimi-K2-Thinking --tensor-parallel-size 8   --enable-prompt-tokens-details   --enable-auto-tool-choice   --enable-prefix-caching   --enable-expert-parallel   --max-num-batched-tokens 32768   --gpu-memory-utilization 0.95   --tool-call-parser kimi_k2   --trust-remote-code --reasoning-parser kimi_k2"
      ]
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "MiniMax-M2": {
    name: "MiniMax-M2",
    title: "MiniMax M2",
    tags: ["Reasoning", "Tool calling"],
    logoUrl:
      "https://cdn-avatars.huggingface.co/v1/production/uploads/676e38ad04af5bec20bc9faf/dUd-LsZEX0H_d4qefO_g6.jpeg",
    description:
      "Reasoning-optimized model. Best for complex logic, math, and multi-step problem solving using native Chain-of-Thought.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    duration_hours: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/bin/bash",
        "-c",
        "python3 -m vllm.entrypoints.openai.api_server --model MiniMaxAI/MiniMax-M2 --tensor-parallel-size 8 --enable-auto-tool-choice --tool-call-parser minimax_m2 --reasoning-parser minimax_m2_append_think --trust-remote-code --max-model-len 8192 --gpu-memory-utilization 0.85 --max-num-batched-tokens 4096 --swap-space 32 --enable-prompt-tokens-details --enable-prefix-caching --max-num-seqs 128 --enforce-eager"
      ]
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.11.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  */

  "DeepSeek-V4-Flash": {
    name: "DeepSeek-V4-Flash",
    title: "DeepSeek V4 Flash",
    tags: ["MoE", "8× H200", "Tool calling"],
    logoUrl:
      "https://cdn-avatars.huggingface.co/v1/production/uploads/6538815d1bdb3c40db94fbfa/xMBly9PUMphrFVMxLX4kq.png",
    description:
      "DeepSeek's 284B MoE (13B active) with native tool calling and reasoning parsers. Fleet-proven vLLM config on 8× H200.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    min_vram_per_card: 141,
    duration_hours: 1,
    gpus_per_container: 8,
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "deepseek-ai/DeepSeek-V4-Flash",
        "--served-model-name",
        "deepseek-ai/DeepSeek-V4-Flash",
        "--trust-remote-code",
        "--tensor-parallel-size",
        "8",
        "--enable-expert-parallel",
        "--kv-cache-dtype",
        "fp8",
        "--block-size",
        "256",
        "--max-num-batched-tokens",
        "16384",
        "--enable-prefix-caching",
        "--tokenizer-mode",
        "deepseek_v4",
        "--tool-call-parser",
        "deepseek_v4",
        "--reasoning-parser",
        "deepseek_v4",
        "--enable-auto-tool-choice"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.21.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Kimi-K2.6": {
    name: "Kimi-K2.6",
    title: "Kimi K2.6",
    tags: ["Agentic", "8× H200", "Multimodal"],
    logoUrl:
      "https://framerusercontent.com/images/EWOn1OXFdxC9nCqiWn4XVhEdo.png?width=512&height=512",
    description:
      "Moonshot K2.6 with tool calling, reasoning, and prefix caching. Multi-GPU vLLM deploy on 8× H200 (~555 GB weights).",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    min_vram_per_card: 141,
    duration_hours: 1,
    gpus_per_container: 8,
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "moonshotai/Kimi-K2.6",
        "--tensor-parallel-size",
        "8",
        "--mm-encoder-tp-mode",
        "data",
        "--compilation_config.pass_config.fuse_allreduce_rms",
        "true",
        "--tool-call-parser",
        "kimi_k2",
        "--reasoning-parser",
        "kimi_k2",
        "--enable-auto-tool-choice",
        "--enable-prefix-caching",
        "--trust-remote-code"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.20.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Kimi-K2.7-Code": {
    name: "Kimi-K2.7-Code",
    title: "Kimi K2.7 Code",
    tags: ["Coding", "8× H200", "Tool calling"],
    logoUrl:
      "https://framerusercontent.com/images/EWOn1OXFdxC9nCqiWn4XVhEdo.png?width=512&height=512",
    description:
      "Code-specialized Kimi K2.7 with native tool calling and reasoning parsers. Requires 8× H200 (~640 GB weights).",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    min_vram_per_card: 141,
    duration_hours: 1,
    gpus_per_container: 8,
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "moonshotai/Kimi-K2.7-Code",
        "--tensor-parallel-size",
        "8",
        "--mm-encoder-tp-mode",
        "data",
        "--compilation_config.pass_config.fuse_allreduce_rms",
        "true",
        "--tool-call-parser",
        "kimi_k2",
        "--reasoning-parser",
        "kimi_k2",
        "--enable-auto-tool-choice",
        "--enable-prefix-caching",
        "--trust-remote-code"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.22.1",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Qwen3.6-27B-FP8": {
    name: "Qwen3.6-27B-FP8",
    title: "Qwen 3.6 27B FP8",
    tags: ["Coding", "1× H100", "Speculative"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Qwen.png",
    description:
      "Dense Qwen 3.6 27B in FP8 with MTP speculative decoding and coder tool parsing. Fits a single H100/H200 (40 GB+).",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    min_vram_per_card: 40,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      env_variables: { VLLM_RPC_TIMEOUT: "60000" },
      entrypoint: [
        "python3",
        "-m",
        "vllm.entrypoints.openai.api_server",
        "--model",
        "Qwen/Qwen3.6-27B-FP8",
        "--served-model-name",
        "Qwen/Qwen3.6-27B",
        "--trust-remote-code",
        "--tensor-parallel-size",
        "1",
        "--gpu-memory-utilization",
        "0.95",
        "--max-num-seqs",
        "128",
        "--max-num-batched-tokens",
        "8192",
        "--enable-prefix-caching",
        "--enable-chunked-prefill",
        "--enable-auto-tool-choice",
        "--tool-call-parser",
        "qwen3_coder",
        "--reasoning-parser",
        "qwen3",
        "--speculative-config.method",
        "mtp",
        "--speculative-config.num_speculative_tokens",
        "1"
      ],
      traffic_port: 8000
    },
    registry_config: {
      image_url: "vllm/vllm-openai:v0.21.0",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Qwen3.6-27B-GGUF-Q4": {
    name: "Qwen3.6-27B-GGUF-Q4",
    title: "Qwen 3.6 27B (Q4)",
    tags: ["Quantized", "Consumer GPU", "llama.cpp"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Qwen.png",
    description:
      "Unsloth UD-Q4_K_XL GGUF on llama.cpp server. ~18 GB VRAM — fits a single L4, A10, T4-24GB, or 3090. Weights auto-pull from Hugging Face.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 18,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "unsloth/Qwen3.6-27B-GGUF:UD-Q4_K_XL",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "GLM-5.2-GGUF-IQ2": {
    name: "GLM-5.2-GGUF-IQ2",
    title: "GLM 5.2 (2-bit)",
    tags: ["Quantized", "2× H200", "llama.cpp"],
    logoUrl:
      "https://cdn-avatars.huggingface.co/v1/production/uploads/62dc173789b4cf157d36ebee/i_pxzM2ZDo3Ub-BEgIkE9.png",
    description:
      "Unsloth's recommended minimum quant (UD-IQ2_M) for GLM-5.2 via llama.cpp. Needs ~245 GB combined VRAM+RAM; comfortable on 2× H200.",
    storage: "500GB-2TB",
    ram: "8-80 GB",
    hardware_name: "H200",
    min_vram_per_card: 141,
    duration_hours: 1,
    gpus_per_container: 2,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "unsloth/GLM-5.2-GGUF:UD-IQ2_M",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--tensor-split",
        "1,1",
        "--ctx-size",
        "16384",
        "--flash-attn",
        "on",
        "--cache-type-k",
        "q4_1",
        "--cache-type-v",
        "q4_1"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Gemma-4-12B-it-GGUF-Q4": {
    name: "Gemma-4-12B-it-GGUF-Q4",
    title: "Gemma 4 12B IT (Q4)",
    tags: ["Quantized", "Consumer GPU", "llama.cpp"],
    logoUrl: "/assets/images/recipes/gemma.png",
    description:
      "Google Gemma 4 12B instruction-tuned Q4_0 QAT GGUF on llama.cpp. ~11 GB VRAM — fits a single consumer GPU.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 11,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "google/gemma-4-12B-it-qat-q4_0-gguf",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on",
        "--jinja"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Qwen3-8B-GGUF-Q8": {
    name: "Qwen3-8B-GGUF-Q8",
    title: "Qwen 3 8B (Q8)",
    tags: ["Quantized", "Consumer GPU", "llama.cpp"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Qwen.png",
    description:
      "Qwen3-8B Q8_0 GGUF on llama.cpp server. ~11 GB VRAM — fits a single L4, A10, or similar consumer GPU.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 11,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "Qwen/Qwen3-8B-GGUF:Q8_0",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on",
        "--jinja"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Ministral-3-8B-Instruct-GGUF-Q8": {
    name: "Ministral-3-8B-Instruct-GGUF-Q8",
    title: "Ministral 3 8B Instruct (Q8)",
    tags: ["Quantized", "Consumer GPU", "llama.cpp"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Ministral.png",
    description:
      "Mistral Ministral 3 8B Instruct (2512) Q8_0 GGUF on llama.cpp. ~12 GB VRAM — fits a single mid-range GPU.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 12,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "mistralai/Ministral-3-8B-Instruct-2512-GGUF:Q8_0",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on",
        "--jinja"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Qwen3.5-9B-GGUF-Q8": {
    name: "Qwen3.5-9B-GGUF-Q8",
    title: "Qwen 3.5 9B (Q8)",
    tags: ["Quantized", "Consumer GPU", "llama.cpp"],
    logoUrl: "https://ionet-ai-model-images.s3.us-west-1.amazonaws.com/models/Qwen.png",
    description:
      "Unsloth Qwen3.5-9B Q8_0 GGUF on llama.cpp server. ~12 GB VRAM — fits a single mid-range GPU.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 12,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "unsloth/Qwen3.5-9B-GGUF:Q8_0",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on",
        "--jinja"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "Laguna-S-2.1-GGUF-IQ2": {
    name: "Laguna-S-2.1-GGUF-IQ2",
    title: "Laguna S 2.1 (IQ2)",
    tags: ["Quantized", "Multi-GPU", "llama.cpp"],
    description:
      "Unsloth Laguna S 2.1 UD-IQ2_M GGUF on llama.cpp server. 2× 48 GB VRAM (e.g. A6000). Weights auto-pull from Hugging Face.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    min_vram_per_card: 48,
    duration_hours: 1,
    gpus_per_container: 2,
    container_config: {
      replica_count: 1,
      entrypoint: [
        "/app/llama-server",
        "-hf",
        "unsloth/Laguna-S-2.1-GGUF:UD-IQ2_M",
        "--host",
        "0.0.0.0",
        "--port",
        "8080",
        "--n-gpu-layers",
        "999",
        "--tensor-split",
        "1,1",
        "--ctx-size",
        "32768",
        "--flash-attn",
        "on",
        "--jinja"
      ],
      traffic_port: 8080
    },
    registry_config: {
      image_url: "ghcr.io/ggml-org/llama.cpp:server-cuda",
      registry_username: "",
      registry_secret: ""
    }
  },
  "MiniMax-H3": {
    name: "MiniMax-H3",
    title: "MiniMax H3",
    tags: ["Video", "ComfyUI", "1× RTX PRO 6000"],
    logoUrl:
      "https://cdn-avatars.huggingface.co/v1/production/uploads/676e38ad04af5bec20bc9faf/dUd-LsZEX0H_d4qefO_g6.jpeg",
    description:
      "Open-weight video generation with synchronised stereo audio (4–15 s, up to 2K). ComfyUI UI in ~2 min; ~42.5 GB weights download in the background. 24 GB VRAM min, 32 GB recommended.",
    storage: "100GB-500GB",
    ram: "8-80 GB",
    hardware_name: "RTX PRO 6000",
    min_vram_per_card: 24,
    duration_hours: 1,
    gpus_per_container: 1,
    container_config: {
      replica_count: 1,
      secret_env_variables: { HF_TOKEN: "" },
      entrypoint: [
        "bash",
        "-lc",
        "curl -fsSL https://gist.githubusercontent.com/rajagurunath-io/62e53f4e0755f7c32da0569a67414a37/raw/401ca125cf2f8c61d44e6c9461f8b9597c173320/minimax_h3_ionet_bootstrap.sh | bash"
      ],
      traffic_port: 8188
    },
    registry_config: {
      image_url: "yanwk/comfyui-boot:cu130-megapak-pt211-20260803",
      registry_username: "",
      registry_secret: ""
    }
  }
};

/** How many recipe tiles to show before “Check More”; keep in sync with active RECIPES keys. */
export const DISPLAY_LIMIT = Object.keys(RECIPES).length;

export const recipeDisplayTitle = (recipe: Pick<RecipeConfig, "title" | "name">): string =>
  recipe.title ?? recipe.name;

export type RecipeKey = keyof typeof RECIPES;
